/**
 * Environment Detection Service
 * Centralized detection and caching of channel (including device type)
 */

// Channel constants to avoid typos
export const CHANNEL_WEBCHAT = 'EVA_webChat';
export const CHANNEL_TEAMS = 'EVA_teams';
export const CHANNEL_SPARQ = 'EVA_sparq';
export const CHANNEL_SPARQ_MINIAPP = 'EVA_sparq_mini';

// Device type constants
export const DEVICE_MOBILE = 'mobile';
export const DEVICE_DESKTOP = 'desktop';

/**
 * Detects the current channel and device type
 * @returns {string} - Returns 'EVA_webChat|desktop', 'EVA_webChat|mobile', 'EVA_teams|desktop', 'EVA_teams|mobile', 'EVA_sparq|desktop', or 'EVA_sparq|mobile'
 */
function detectChannelAndDevice() {
    const urlParams = new URLSearchParams(window.location.search);
    // Check if we're in an iframe
    const isInIframe = window.parent && window.parent !== window;
    const normalizedPathname = window.location.pathname.toLowerCase();
    const isSmartAnswerPage = normalizedPathname === '/smart-answer' || normalizedPathname === '/smartanswer';
    
    const isTeams = urlParams.get('teams') === '1';
    
    // Check if running in iframe and parent URL contains "sparq"
    let isSparq = false;
    let isSparqMini = false;
    let parentUrl = '';

    try {
        parentUrl = window.parent.location.href.toLowerCase();
        isSparqMini = parentUrl.includes('sparq-online');
        if (isSmartAnswerPage && isInIframe && !isTeams) {
            isSparq = true;
        } else if (isInIframe && !isTeams) {
            // Non-SmartAnswer iframe detection is based on the accessible parent URL.
            isSparq = true;
            isSparq = parentUrl.includes('sparq');
        }
    } catch (e) {
        // Cross-origin iframe access can fail. SmartAnswer iframes still map to Sparq.
        if (isSmartAnswerPage && isInIframe && !isTeams) {
            isSparq = true;
        }
        console.debug('Cannot access parent URL due to cross-origin restrictions');
    }    
    // Detect device type
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    const mobileRegex = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile|mobile|CriOS/i;
    const isMobileUserAgent = mobileRegex.test(userAgent);
    const isMobileScreen = window.innerWidth <= 768;
        
    // Prioritize user agent over screen width when in iframe to avoid false positives
    // (e.g., narrow iframes on desktop browsers shouldn't be detected as mobile)
    const deviceType = isInIframe 
        ? (isMobileUserAgent ? DEVICE_MOBILE : DEVICE_DESKTOP)
        : ((isMobileUserAgent || isMobileScreen) ? DEVICE_MOBILE : DEVICE_DESKTOP);
    
    // Determine channel priority: Teams > SparqMiniApp > Sparq > WebChat
    let channel;
    // For sparq contexts, derive hostId and hostUrl from the parent page URL
    const resolvedParentUrl = parentUrl || document.referrer || '';
    const hostId = (isSparqMini) ? 'sparq' : window.location.hostname;
    const hostUrl = (isSparqMini) && resolvedParentUrl
        ? resolvedParentUrl.split('?')[0]
        : window.location.origin;
    if (isTeams) {
        channel = CHANNEL_TEAMS;
    } else if (isSparqMini) {
        channel = CHANNEL_SPARQ_MINIAPP;
    } else if (isSparq) {
        channel = CHANNEL_SPARQ;
    } else {
        channel = CHANNEL_WEBCHAT;
    }
    
    //  Return object - avoids pipe-split URL issues and unused var warning
    return { channel, deviceType, hostId, hostUrl };
}

/**
 * Initializes environment detection and caches results in sessionStorage
 * Should be called once when the app starts
 */
export function initializeEnvironmentDetection() {
    // Destructure directly from object - no more split('|')
    const { channel, deviceType, hostId, hostUrl } = detectChannelAndDevice();
    const channelAndDevice = `${channel}|${deviceType}`; // backward compat (no URLs)
    const isTeamsEnvironment = channel === CHANNEL_TEAMS;
    const isSparqMiniAppEnvironment = channel === CHANNEL_SPARQ_MINIAPP;
    
    // Cache in sessionStorage
    sessionStorage.setItem('detectedChannelAndDevice', channelAndDevice);
    sessionStorage.setItem('isTeamsEnvironment', isTeamsEnvironment.toString());
    sessionStorage.setItem('isSparqMiniAppEnvironment', isSparqMiniAppEnvironment.toString());
    sessionStorage.setItem('hostId', hostId || '');
    sessionStorage.setItem('hostUrl', hostUrl || '');
    
    console.log('🎯 Environment Detection Complete:', {
        channelAndDevice,
        isTeamsEnvironment,
        isSparqMiniAppEnvironment,
        hostId,
        hostUrl
    });
    
    return {
        channel: channelAndDevice, // For backward compatibility
        isTeamsEnvironment,
        isSparqMiniAppEnvironment,
        hostId,
        hostUrl
    };
}

/**
 * Gets the current channel and device from cache or detects it
 * @returns {string} - Returns 'EVA_webChat|mobile', 'EVA_webChat|desktop', 'EVA_teams|mobile', or 'EVA_teams|desktop'
 */
export function getChannelAndDevice() {
    let channelAndDevice = sessionStorage.getItem('detectedChannelAndDevice');
    if (!channelAndDevice) {
        // Now builds string from object
        const { channel, deviceType } = detectChannelAndDevice();
        channelAndDevice = `${channel}|${deviceType}`;
        sessionStorage.setItem('detectedChannelAndDevice', channelAndDevice);
        console.debug("Reactor: Channel and device detected (fresh):", channelAndDevice);
    }
    return channelAndDevice;
}

/**
 * Gets the channel without device type
 * @returns {string} - Returns CHANNEL_WEBCHAT, CHANNEL_TEAMS, CHANNEL_SPARQ, or CHANNEL_SPARQ_MINIAPP
 */
export function getChannel() {
    const channelAndDevice = getChannelAndDevice();
    const [channel] = channelAndDevice.split('|');
    return channel;
}

/**
 * Gets the device type
 * @returns {string} - Returns DEVICE_MOBILE or DEVICE_DESKTOP
 */
export function getDeviceType() {
    const channelAndDevice = getChannelAndDevice();
    const [, deviceType] = channelAndDevice.split('|');
    return deviceType;
}

/**
 * Gets whether the app is running in Teams environment
 * @returns {boolean}
 */
export function getIsTeamsEnvironment() {
    const stored = sessionStorage.getItem('isTeamsEnvironment');
    if (stored !== null) {
        return stored === 'true';
    }
    
    // Fallback to detecting from channel
    const channel = getChannel();
    const isTeams = channel === CHANNEL_TEAMS;
    sessionStorage.setItem('isTeamsEnvironment', isTeams.toString());
    return isTeams;
}

/**
 * Gets whether the app is running in Sparq Mini App environment
 * @returns {boolean}
 */
export function getIsSparqMiniAppEnvironment() {
    const stored = sessionStorage.getItem('isSparqMiniAppEnvironment');
    if (stored !== null) {
        return stored === 'true';
    }
    
    // Fallback to detecting from channel
    const channel = getChannel();
    const isSparqMiniApp = channel === CHANNEL_SPARQ_MINIAPP;
    sessionStorage.setItem('isSparqMiniAppEnvironment', isSparqMiniApp.toString());
    return isSparqMiniApp;
}

/**
 * Gets the operating system
 * @returns {string} - Returns 'macOS', 'Windows', 'Linux', or 'Unknown'
 * This function is currently not used but kept for future use
 * Will be used to hide VoiceToText on Edge for macOS
 * macOS does not support the Edge Speech Recogntion API/extension
 */
export function getOperatingSystem() {
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    
    if (/Mac OS X|Macintosh|iPhone|iPad|iPod/.test(userAgent)) {
        return 'macOS';
    } 
    else if (/Windows NT|Win64|Win32|WOW64/.test(userAgent)) {
        return 'Windows';
    } 
    else if (/Linux/.test(userAgent) && !/Android/.test(userAgent)) {
        return 'Linux';
    }
    return 'Unknown';
}

/**
 * Detects if the browser is Microsoft Edge
 * @returns {boolean}
 */
export function isEdgeBrowser() {
    const userAgent = navigator.userAgent || '';
    return /Edg/.test(userAgent);
}

/**
 * Determines if livechat feature should be enabled based on the current environment
 * @returns {boolean} - Returns true if livechat should be enabled, false otherwise
 */
export function isLiveChatEnabled() {
    const channel = getChannel();
    // Disable livechat for SPARQ and SPARQ Mini App environments
    if (channel === CHANNEL_SPARQ) {
        return false;
    }
    // Enable livechat for other environments (WebChat and Teams)
    return true;
}

/**
 * Clears cached environment detection (useful for testing)
 */
export function clearEnvironmentCache() {
    sessionStorage.removeItem('detectedChannelAndDevice');
    sessionStorage.removeItem('isTeamsEnvironment');
    sessionStorage.removeItem('isSparqMiniAppEnvironment');
    sessionStorage.removeItem('hostId');
    sessionStorage.removeItem('hostUrl');
    console.log('Environment detection cache cleared');
}

/**
 * Gets the captured landing source data (referrer classification).
 * Captured by the captureLandingSource IIFE in index.js before MSAL auth redirect.
 * @returns {{ referrer: string, referrerType: string, fromSmartAnswer: boolean, landingPath: string, timestamp: number } | null}
 */
export function getLandingSource() {
    try {
        const stored = sessionStorage.getItem('landing_source');
        return stored ? JSON.parse(stored) : null;
    } catch (error) {
        console.warn('Failed to parse landing_source from sessionStorage:', error);
        return null;
    }
}